import { Action, Mutation, Module } from "vuex-class-modules";
import Axios, { AxiosResponse } from "axios";
import AttachedDeviceModel from "@/models/AttachedDeviceModel";
import AxiosSecureModule from "./axiosSecureModule";
import { Log } from "@/plugins/Logging";
import { app, enrollState } from "@/store";
import { unpairUi } from "@/store/unpairUi";

@Module({ generateMutationSetters: true })
export default class SensorModule extends AxiosSecureModule {
    private static readonly ACTION_TIMEOUT_MS = 30000;
    private lastReconnectNoticeAt = 0;

    // --- State ---
    devices: Array<AttachedDeviceModel> = [];

    // If a device is expected to appear "connecting" right after load,
    // keep its id here until SetData() hydrates devices from server.
    private pendingConnectingDeviceIds = new Set<string>();

    // Device status ACK timestamps to avoid reverting from stale DB reads
    private lastStatusAckAt = new Map<string, number>(); // deviceId -> epoch ms

    // Pending updates for devices not yet in the local list
    private pendingAgeUpdates = new Map<string, number>();
    private pendingStatusUpdates = new Map<string, boolean | null>();

    // Per-device sync watchdogs (spinners/timeouts)
    private syncTimers = new Map<string, number>();   // deviceId -> interval id
    private syncSeconds = new Map<string, number>();  // deviceId -> seconds

    // Per-device ping watchdogs (red pulsing dot while waiting for a response)
    private pingTimeouts = new Map<string, number>(); // deviceId -> timeout id

    // Called when sensor is reconnecting
    private maybeNotifyReconnectInfo() {
        const now = Date.now();
        const RECONNECT_NOTICE_MS = 15 * 60 * 1000;
        if (now - this.lastReconnectNoticeAt >= RECONNECT_NOTICE_MS) {
            // use whatever toast you already show; you've used app.success/app.error earlier.
            // If you have app.info or a snackbar helper, call that instead.
            app.success = "After a restart, sensors can take up to 10 minutes to reconnect.";
            this.lastReconnectNoticeAt = now;
        }
    }

    // --- Internal: replace device object (Vue 2 reactive for new keys) ---
    private updateDevice(deviceId: string, patch: Record<string, any>) {
        const idx = this.devices.findIndex(d => d.attachedDeviceId === deviceId);
        if (idx === -1) return;

        const current = this.devices[idx] as any;
        const next = { ...current, ...patch }; // NEW object => new reactive shape
        // Replace the array slot so Vue 2 reactivity picks up *new* keys
        this.devices.splice(idx, 1, next);
    }

    // --- Private helpers (UI-only flags stored on device objects) ---
    private markBusy(deviceId: string, busy: boolean) {
        this.updateDevice(deviceId, { isBusy: busy });
    }

    private startDeviceSync(deviceId: string) {
        this.stopDeviceSync(deviceId);  // reset if already running
        this.markBusy(deviceId, true);
        this.syncSeconds.set(deviceId, 0);

        const tick = () => {
            const s = (this.syncSeconds.get(deviceId) ?? 0) + 1;
            this.syncSeconds.set(deviceId, s);
            if (s * 1000 >= SensorModule.ACTION_TIMEOUT_MS) {
                app.error = "Sensor unresponsive.";

                // Revert toggle to last stable state (what we knew from ACK/DB)
                const d = this.devices.find(x => x.attachedDeviceId === deviceId) as any;
                if (d) {
                    const fallback = d.stableStatus ?? d.status;
                    this.updateDevice(deviceId, { status: fallback });
                }

                this.stopDeviceSync(deviceId);
                this.GetData(); // refresh list as a fallback
            }
        };

        const id = window.setInterval(tick, 1000);
        this.syncTimers.set(deviceId, id);
    }

    private stopDeviceSync(deviceId: string) {
        const id = this.syncTimers.get(deviceId);
        if (id) {
            clearInterval(id);
            this.syncTimers.delete(deviceId);
        }
        this.syncSeconds.delete(deviceId);
        this.markBusy(deviceId, false);
    }

    private markPinging(deviceId: string, pinging: boolean) {
        this.updateDevice(deviceId, { isPinging: pinging });
    }

    // Ping watchdog: a single deterministic timeout (not a tick counter) so the red pulse
    // is guaranteed to clear and report failure after ACTION_TIMEOUT_MS, no matter what
    // else happens to the device list in the meantime (polling, SignalR pushes, etc.).
    private startPingWatch(deviceId: string) {
        this.stopPingWatch(deviceId);
        this.markPinging(deviceId, true);

        const timeoutId = window.setTimeout(() => {
            this.pingTimeouts.delete(deviceId);
            this.markPinging(deviceId, false);
            app.error = "Ping failed: sensor did not respond in time. It may be out of range or powered off.";
        }, SensorModule.ACTION_TIMEOUT_MS);

        this.pingTimeouts.set(deviceId, timeoutId);
    }

    private stopPingWatch(deviceId: string) {
        const id = this.pingTimeouts.get(deviceId);
        if (id) {
            clearTimeout(id);
            this.pingTimeouts.delete(deviceId);
        }
        this.markPinging(deviceId, false);
    }

    // --- API calls / Actions ---
    @Action
    public async GetData() {
        await this.defineHeaderAxios();
        Axios.get("/api/sensor")
            .then((result: AxiosResponse) => {
                const payload = result.data as any;
                const list = Array.isArray(payload)
                    ? payload
                    : Array.isArray(payload?.devices)
                        ? payload.devices
                        : null;
                const contentType = result.headers["content-type"] ?? "";
                const isHtmlResponse = typeof payload === "string" &&
                    (contentType.toLowerCase().includes("text/html") ||
                        payload.trimStart().toLowerCase().startsWith("<!doctype html"));
                if (isHtmlResponse) {
                    Log.error("Get sensor data failed: HTML response", [{
                        status: result.status,
                        url: result.config.url,
                        contentType,
                    }]);
                    //app.error = "Sensor data unavailable. Please refresh the page.";
                    return;
                }
                if (!list) {
                    Log.error("Get sensor data failed: unexpected payload", [payload]);
                    //app.error = "Sensor data unavailable. Please refresh the page.";
                    return;
                }
                this.SetData(list);
            })
            .catch((error) => Log.error("Get sensor data failed", error));
    }

    /**
     * Toggle a device (switch/relay/etc.).
     * For door/hatch (or anything that shouldn't show a spinner), pass noSync: true.
     */
    @Action
    public async SetDeviceStatus(payload: { id: string; status: boolean; noSync?: boolean }) {
        if (!payload.noSync) {
            // Start per-device spinner while server processes the toggle
            this.startDeviceSync(payload.id);
        }

        await this.defineHeaderAxios();
        Axios.put(`api/sensor/status/${payload.id}/${payload.status}`)
            .then(() => {
                Log.information("SetDeviceStatus sent.");
                // We stop the spinner on ACKs via SetSensorStatus/SetSensorAge.
            })
            .catch((error) => {
                if (!payload.noSync) {
                    this.stopDeviceSync(payload.id);
                }
                Log.error("SetDeviceStatus failed", error);
            });
    }

    /**
     * Door/hatch control: same endpoint as switches but WITHOUT sync/busy.
     */
    @Action
    public async SetDoorState(payload: { id: string; state: boolean }) {
        return this.SetDeviceStatus({ id: payload.id, status: payload.state, noSync: true });
    }

    @Action
    public async SetHatchState(payload: { id: string; state: boolean }) {
        return this.SetDeviceStatus({ id: payload.id, status: payload.state, noSync: true });
    }

    @Action
    public async GetCoreHistory() {
        await this.defineHeaderAxios();
        Axios.get(`/api/sensor/CoreHistory`)
            .then(() => Log.information("Core history fetched"))
            .catch((error) => Log.error("GetCoreHistory failed", error));
    }

    @Action
    public async GetDeviceHistory(id: string) {
        await this.defineHeaderAxios();
        Axios.get(`/api/sensor/history/${id}`)
            .then(() => Log.information("Device history fetched"))
            .catch((error) => Log.error("GetDeviceHistory failed", error));
    }

    @Action
    public async RequestAdcCalibration(channel: number) {
        await this.defineHeaderAxios();
        try {
            await Axios.get(`api/sensor/calibrate/${channel}`);
            Log.information("ADC calibration requested");
            return { ok: true };
        } catch (error: any) {
            Log.error("RequestAdcCalibration failed", error);
            const message = error?.response?.data;
            const detail = typeof message === "string" && message.trim().length > 0
                ? message
                : "Calibration request failed.";
            return { ok: false, message: detail };
        }
    }

    // ACK from server/push: update status and stop spinner for this device
    @Action
    public async SetSensorStatus(payload: { device: string; status: boolean | null; tampered?: boolean }) {
        const dev = this.devices.find(d => d.attachedDeviceId === payload.device) as any;
        if (!dev) {
            this.pendingStatusUpdates.set(payload.device, payload.status);
            Log.information(`SensorModule: status queued id=${payload.device}, status=${payload.status}`);
            this.GetData();
            return;
        }

        const nextStable = payload.status !== null ? payload.status : (dev?.stableStatus ?? null);
        const changes: any = { status: payload.status, stableStatus: nextStable };
        if (payload.tampered !== undefined) changes.tampered = payload.tampered;
        this.updateDevice(payload.device, changes);
        this.lastStatusAckAt.set(payload.device, Date.now());
        this.stopDeviceSync(payload.device);
        Log.information(`SensorModule: status updated id=${payload.device}, status=${payload.status}, tampered=${payload.tampered}`);
    }

    // Battery updates do not necessarily indicate action ACK; do not stop sync
    @Action
    public async SetSensorBattery(payload: { device: string; battery: number }) {
        this.updateDevice(payload.device, { battery: payload.battery });
        Log.information(`SensorModule: battery updated id=${payload.device}, battery=${payload.battery}`);
    }

    // Age updates: when age becomes valid (not -1), device answered -> stop spinner
    @Action
    public async SetSensorAge(payload: {
        device: string;
        status?: string;
        age: string;
        atPosition?: any;
    }) {
        if (this.devices.length === 0) {
            Log.information("SensorModule: No devices paired.");
            return;
        }
        const dev = this.devices.find(d => d.attachedDeviceId === payload.device) as any;
        if (!dev) {
            const ageNum = Number(payload.age);
            this.pendingAgeUpdates.set(payload.device, ageNum);
            Log.information(`SensorModule: age queued id=${payload.device}, age=${ageNum}`);
            this.GetData();
            return;
        }

        const prevConnecting = !!dev.isConnecting;             // <-- track previous
        const ageNum = Number(payload.age);
        const isConnecting = ageNum === -1;
        const pairedAt = dev?.pairedAt ?? Date.now();

        this.updateDevice(payload.device, { age: ageNum, isConnecting, pairedAt });

        if (isConnecting && !prevConnecting) {                 // <-- notify on flip
            //this.maybeNotifyReconnectInfo();
        }

        if (!isConnecting) this.stopDeviceSync(payload.device);
        if (ageNum <= 1) this.stopPingWatch(payload.device);   // device answered; clear the red pulse

        Log.information(
            `SensorModule: age updated id=${payload.device}, age=${ageNum}, connecting=${isConnecting}`
        );
    }

    @Action
    public async GetSensorAge(id: string) {
        const dev = this.devices.find(item => item.attachedDeviceId === id);
        return dev?.age;
    }

    /**
     * Ping/refresh an aged (unresponsive) sensor: asks the device to try to reach the sensor
     * again. Glows the status dot red (via isPinging) for the same window we wait for a reply,
     * and surfaces a notification if it times out.
     */
    @Action
    public async PingDevice(attachedDeviceId: string) {
        const dev = this.devices.find(d => d.attachedDeviceId === attachedDeviceId) as any;
        if (!dev || dev.isPinging) return;

        await this.defineHeaderAxios();
        this.startPingWatch(attachedDeviceId);
        Axios.get(`api/sensor/ping/${encodeURIComponent(attachedDeviceId)}`)
            .then(() => Log.information("Ping sent."))
            .catch((error) => {
                this.stopPingWatch(attachedDeviceId);
                Log.error("Ping failed.", error);
                app.error = "Ping request failed.";
            });
    }

    @Action
    public async UnpairDevice(attachedDeviceId: string) {
        await this.defineHeaderAxios();
        // unpair can be slow; show spinner for the device
        this.startDeviceSync(attachedDeviceId);
        unpairUi.open(attachedDeviceId);
        Axios.delete(`api/sensor/unpair/${encodeURIComponent(attachedDeviceId)}`)
            .then(() => {
                Log.information("Unpair sent.");
                this.GetData();
                // Follow-up refresh to catch LEAVE processing delay, 5x every 2 seconds
                for (let i = 1; i <= 5; i++) {
                    window.setTimeout(() => this.GetData(), i * 2000);
                }
            })
            .catch((error) => {
                this.stopDeviceSync(attachedDeviceId);
                unpairUi.complete(attachedDeviceId);
                Log.error("Unpair failed.", error);
                app.error = "Unpair failed";
            });
    }

    // --- Mutations ---
    @Mutation
    SetData(data: Array<AttachedDeviceModel> | { devices?: Array<AttachedDeviceModel> } | null) {
        const list = Array.isArray(data)
            ? data
            : Array.isArray((data as any)?.devices)
                ? (data as any).devices
                : null;
        if (!list) {
            Log.error("SetData expected array", data as any);
            return;
        }
        const now = Date.now();
        const SAFETY_MS = 2000; // ignore older DB rows for 2s after an ACK
        const DOOR_PAIRING_UNKNOWN_MS = 60 * 1000;
        const pairingTypeIdRaw = enrollState.EnrolledDeviceTypeId ?? enrollState.lastEnrolledDeviceTypeId;
        const pairingTypeId = pairingTypeIdRaw ? Number(pairingTypeIdRaw) : null;
        const inPairingWindow =
            enrollState.lastEnrollAt !== null &&
            now - enrollState.lastEnrollAt < DOOR_PAIRING_UNKNOWN_MS;

        const withUi = list.map(d => {
            const prev = this.devices.find(x => x.attachedDeviceId === d.attachedDeviceId) as any;

            // Map DB 'alarm1' to our UI 'status' (true = on/open)
            const dbStatus = typeof (d as any).alarm1 === "boolean" ? (d as any).alarm1 : null;
            const isNewDevice = (d as any).latestEventName === "NEW_DEV_ADDED";

            // If we recently got an ACK for this device and the DB row looks older,
            // keep the in-memory status to avoid flicker/flipping back.
            const ackAt = this.lastStatusAckAt.get(d.attachedDeviceId || "") || 0;
            const serverMod = Date.parse((d as any).modifiedDateTime || "1970-01-01T00:00:00Z");
            const usePrevStatus = prev?.status !== undefined && (serverMod + SAFETY_MS) < ackAt;

            const pairingMatches =
                pairingTypeId !== null &&
                !Number.isNaN(pairingTypeId) &&
                d.attachedDeviceTypeId === pairingTypeId;
            const shouldForceUnknown =
                inPairingWindow && pairingMatches && (prev?.stableStatus === null || prev?.stableStatus === undefined);

            const status = usePrevStatus
                ? prev.status
                : shouldForceUnknown
                    ? null
                    : isNewDevice
                        ? null
                        : dbStatus;
            const stableStatus = dbStatus !== null ? dbStatus : (prev?.stableStatus ?? prev?.status ?? null);
            const pairedAt = prev?.pairedAt ?? now;

            return {
                ...(d as any),
                status,
                stableStatus,
                pairedAt,
                isBusy: prev?.isBusy ?? false,
                isPinging: prev?.isPinging ?? false,
                isConnecting: (d as any).age === -1
            };
        });

        this.devices.splice(0, this.devices.length, ...withUi);

        // Clear any red pinging pulse for devices that have answered since the last poll.
        withUi.forEach(d => {
            if ((d as any).age <= 1) this.stopPingWatch(d.attachedDeviceId as string);
        });

        let anyNewConnecting = false;
        withUi.forEach(d => {
            const prev = this.devices.find(x => x.attachedDeviceId === d.attachedDeviceId) as any;
            // (If you prefer, track prev before splice; or just check d.isConnecting)
            if ((d as any).isConnecting === true) anyNewConnecting = true;
        });
        //if (anyNewConnecting) this.maybeNotifyReconnectInfo();

        // Re-apply any queued connecting flags
        this.pendingConnectingDeviceIds.forEach(id => {
            this.updateDevice(id, { isConnecting: true });
        });
        this.pendingConnectingDeviceIds.clear();

        // Apply queued age updates when device appears
        this.pendingAgeUpdates.forEach((ageNum, id) => {
            const dev = this.devices.find(d => d.attachedDeviceId === id) as any;
            if (!dev) return;
            const isConnecting = ageNum === -1;
            this.updateDevice(id, { age: ageNum, isConnecting });
            if (!isConnecting) this.stopDeviceSync(id);
            if (ageNum <= 1) this.stopPingWatch(id);
            this.pendingAgeUpdates.delete(id);
        });

        // Apply queued status updates when device appears
        this.pendingStatusUpdates.forEach((status, id) => {
            const dev = this.devices.find(d => d.attachedDeviceId === id) as any;
            if (!dev) return;
            const nextStable = status !== null ? status : (dev?.stableStatus ?? null);
            this.updateDevice(id, { status, stableStatus: nextStable });
            this.lastStatusAckAt.set(id, Date.now());
            this.stopDeviceSync(id);
            this.pendingStatusUpdates.delete(id);
        });

        // Devices removed server-side (LEAVE) must not keep their watchdog running.
        Array.from(this.syncTimers.keys()).forEach(id => {
            if (!this.devices.some(d => d.attachedDeviceId === id)) this.stopDeviceSync(id);
        });
    }

    @Mutation
    SetConnectingState(deviceId: string) {
        const dev = this.devices.find(d => d.attachedDeviceId === deviceId) as any;

        if (!dev) {
            // device list not hydrated yet — queue and still show the toast
            this.pendingConnectingDeviceIds.add(deviceId);       // <-- queue
            //this.maybeNotifyReconnectInfo();                     // <-- notify
            return;
        }

        const wasConnecting = !!dev.isConnecting;              // <-- track previous
        this.updateDevice(deviceId, { isConnecting: true });

        if (!wasConnecting) {                                  // <-- notify on flip
            //this.maybeNotifyReconnectInfo();
        }
    }

    @Mutation
    ClearConnectingState(deviceId: string) {
        this.updateDevice(deviceId, { isConnecting: false });
    }
}